import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, Plus, MailWarning, History, ArrowRight } from "lucide-react";
import { ActivityItem } from "@/components/ActivityItem";
import { api, errorMessage, timeAgo } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function Dashboard() {
  const [activity, setActivity] = useState(null);
  useEffect(() => { api.get("/admin/activity", { params: { limit: 10 } }).then((r) => setActivity(r.data)).catch(() => setActivity([])); }, []);
  const [stats, setStats] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [savingInterval, setSavingInterval] = useState(false);

  const saveInterval = async (minutes) => {
    setSavingInterval(true);
    try {
      await api.put("/admin/sync-interval", { minutes });
      setStats((s) => ({ ...s, sync_interval_minutes: minutes }));
      toast.success(`Audio feeds will now sync every ${minutes === 120 ? "2 hours" : `${minutes} minutes`}`);
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSavingInterval(false); }
  };

  const load = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const syncAll = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/sync-all");
      toast.success(`Synced ${data.shows_synced} feeds · ${data.episodes_added} new episodes`);
      load();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSyncing(false); }
  };

  const cards = stats ? [["Shows", stats.shows, "shows"], ["Episodes", stats.episodes, "episodes"], ["Team members", stats.team, "team"], ["New applications", stats.applications ?? 0, "applications"]] : [];

  return (
    <div data-testid="admin-dashboard">
      <p className="eyebrow mb-2">Overview</p>
      <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Dashboard</h1>
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(([label, value, id]) => (
          <div key={label} className="card-surface rounded-2xl p-5" data-testid={`stat-${id}`}>
            <p className="text-xs uppercase tracking-widest text-slate-400 font-mono">{label}</p>
            <p className="mt-2 font-display text-4xl font-black">{value}</p>
          </div>
        ))}
      </div>
      <div className="mt-8 card-surface rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p className="font-semibold">RSS feeds</p>
          <p className="text-sm text-slate-400">Audio feeds auto-sync every {stats?.sync_interval_minutes || 60} minutes; YouTube playlists & TikTok every {(stats?.video_sync_interval_minutes || 240) / 60} hours. Last sync {stats?.last_sync ? timeAgo(stats.last_sync) : "never"}.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <select value={stats?.sync_interval_minutes || 60} onChange={(e) => saveInterval(Number(e.target.value))} disabled={!stats || savingInterval} className="h-10 rounded-full bg-white/5 border border-white/15 px-4 text-sm font-medium disabled:opacity-50" aria-label="Audio feed sync interval" data-testid="sync-interval-select">
            {[[30, "Every 30 min"], [60, "Every 60 min"], [120, "Every 2 hours"]].map(([v, l]) => <option key={v} value={v} className="bg-[#0B1A33]">{l}</option>)}
          </select>
          <Button onClick={syncAll} disabled={syncing} variant="outline" className="rounded-full border-white/15 bg-transparent hover:bg-white/10" data-testid="sync-all-button">
            <RefreshCw size={16} className={`mr-2 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync all feeds now"}
          </Button>
        </div>
      </div>
      {stats && !stats.email_configured && (
        <div className="mt-4 rounded-2xl border border-amber-400/30 bg-amber-400/10 p-4 flex items-start gap-3 text-sm" data-testid="email-not-configured-notice">
          <MailWarning size={18} className="text-amber-300 shrink-0 mt-0.5" />
          <p className="text-amber-100">Application email alerts are not active yet — add a <span className="font-mono">RESEND_API_KEY</span> to the server to receive pitches by email. Pitches are still saved under <Link to="/admin/applications" className="underline">Applications</Link>.</p>
        </div>
      )}
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <Link to="/admin/shows?new=1" className="card-surface card-surface-hover rounded-2xl p-6 flex items-center gap-4" data-testid="quick-add-show">
          <span className="h-11 w-11 rounded-full bg-sky-400 text-[#070F1E] flex items-center justify-center"><Plus /></span>
          <div><p className="font-semibold">Submit a new show</p><p className="text-sm text-slate-400">Paste an RSS feed and it appears on the front page.</p></div>
        </Link>
        <Link to="/admin/episodes?new=1" className="card-surface card-surface-hover rounded-2xl p-6 flex items-center gap-4" data-testid="quick-add-episode">
          <span className="h-11 w-11 rounded-full bg-amber-400 text-[#070F1E] flex items-center justify-center"><Plus /></span>
          <div><p className="font-semibold">Post an episode manually</p><p className="text-sm text-slate-400">For shows without a feed, or one-off specials.</p></div>
        </Link>
      </div>
      <section className="mt-6 card-surface rounded-2xl p-5" data-testid="recent-activity">
        <div className="flex items-center justify-between gap-3 px-1">
          <div>
            <p className="eyebrow mb-0.5 flex items-center gap-1.5"><History size={12} /> Recent updates</p>
            <p className="text-xs text-slate-500">The 10 latest changes across shows, episodes, hosts, submissions and the site.</p>
          </div>
          <Link to="/admin/activity" className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-1.5 text-xs font-bold text-slate-200 hover:bg-white/10 shrink-0" data-testid="activity-view-all">Past 7 days <ArrowRight size={12} /></Link>
        </div>
        <ul className="mt-3 space-y-0.5" data-testid="recent-activity-list">
          {activity === null && <li className="px-3 py-2 text-sm text-slate-500">Loading…</li>}
          {activity !== null && activity.length === 0 && <li className="px-3 py-2 text-sm text-slate-500" data-testid="recent-activity-empty">No updates logged yet — changes you and your hosts make will show up here.</li>}
          {(activity || []).map((item, i) => <ActivityItem key={item.id} item={item} index={i} />)}
        </ul>
      </section>
    </div>
  );
}
